import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Calendar,
  Clock,
  MapPin,
  Music,
  Wine,
  Gift,
  Sparkles,
  Mic,
  Heart,
  ArrowRight,
  Bell,
  Loader2,
  CheckCircle2,
} from "lucide-react";
import useScrollReveal from "@/hooks/useScrollReveal";
import { PastEventsGallery } from "@/components/PastEventsGallery";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FLYER_URL =
  "https://customer-assets.emergentagent.com/job_gen-connect-11/artifacts/fgxw0zum_unfiltered%20minds%20event.jpg";

function Reveal({ children, className = "", as: As = "div", stagger = false }) {
  const [ref, visible] = useScrollReveal();
  return (
    <As
      ref={ref}
      className={`${stagger ? "reveal-stagger" : "reveal"} ${visible ? "is-visible" : ""} ${className}`}
    >
      {children}
    </As>
  );
}

function EventEmailSignup() {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [city, setCity] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await axios.post(`${API}/events/signup`, {
        email,
        name: name || null,
        city: city || null,
      });
      setDone(true);
      toast.success("You're on the list. See you soon.");
    } catch (err) {
      toast.error(
        err?.response?.data?.detail?.[0]?.msg || "Something went wrong. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="py-20 lg:py-28" data-testid="event-signup-section">
      <div className="max-w-5xl mx-auto px-6 lg:px-10">
        <div className="relative bg-gradient-navy rounded-[2.5rem] p-10 lg:p-14 overflow-hidden">
          <div className="absolute -top-20 -right-20 w-[400px] h-[400px] bg-teal/20 rounded-full blur-3xl" aria-hidden="true" />
          <div className="absolute -bottom-20 -left-20 w-[400px] h-[400px] bg-teal/10 rounded-full blur-3xl" aria-hidden="true" />

          <Reveal className="relative grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-5">
              <div className="w-14 h-14 rounded-2xl bg-teal/20 backdrop-blur text-teal flex items-center justify-center mb-6">
                <Bell className="w-7 h-7" />
              </div>
              <p className="eyebrow !text-teal mb-3">Don't miss the next one</p>
              <h2 className="font-display text-4xl sm:text-5xl text-white leading-[1.05] mb-5">
                Get the next event <span className="hl-teal">first.</span>
              </h2>
              <p className="text-white/75 leading-relaxed">
                We're rolling out community events across the country. Drop your email and we'll let you know
                the moment one hits a city near you — no spam, no clinical jargon, just an invitation.
              </p>
            </div>

            <div className="lg:col-span-7">
              {done ? (
                <div
                  className="bg-white/10 border border-white/20 rounded-2xl p-8 text-center backdrop-blur"
                  data-testid="event-signup-success"
                >
                  <div className="w-14 h-14 rounded-full bg-teal flex items-center justify-center mx-auto mb-4">
                    <CheckCircle2 className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="font-display text-2xl text-white mb-2">You're on the list.</h3>
                  <p className="text-white/75 text-sm">
                    The next event invite will land in your inbox before it goes anywhere else.
                  </p>
                </div>
              ) : (
                <form
                  onSubmit={submit}
                  className="bg-white/5 border border-white/15 rounded-2xl p-6 sm:p-8 backdrop-blur space-y-3"
                  data-testid="event-signup-form"
                >
                  <input
                    type="email"
                    required
                    placeholder="you@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="gh-input !bg-white/10 !border-white/20 !text-white placeholder:!text-white/50"
                    data-testid="event-signup-email"
                  />
                  <div className="grid sm:grid-cols-2 gap-3">
                    <input
                      type="text"
                      placeholder="First name (optional)"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className="gh-input !bg-white/10 !border-white/20 !text-white placeholder:!text-white/50"
                      data-testid="event-signup-name"
                    />
                    <input
                      type="text"
                      placeholder="City (optional)"
                      value={city}
                      onChange={(e) => setCity(e.target.value)}
                      className="gh-input !bg-white/10 !border-white/20 !text-white placeholder:!text-white/50"
                      data-testid="event-signup-city"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={loading}
                    className="btn-primary w-full justify-center"
                    data-testid="event-signup-submit"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" /> Adding you...
                      </>
                    ) : (
                      <>
                        Notify me of future events <ArrowRight className="w-4 h-4" />
                      </>
                    )}
                  </button>
                  <p className="text-[11px] text-white/50 text-center">
                    We respect your inbox. Event invites only — unsubscribe anytime.
                  </p>
                </form>
              )}
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

const FEATURED_EVENT = {
  id: "unfiltered-minds",
  name: "Unfiltered Minds",
  tagline: "A nationwide mental health open mic.",
  subtagline: "Come to share or come to listen.",
  date: "May 5, 2026",
  dateShort: "MAY 5",
  time: "6:30 – 8:00 PM",
  location: "615 E Morehead St, Floor 24",
  city: "Charlotte, NC 28202",
  hosts: ["Madina PLLC", "Generational Health"],
  description:
    "An honest, beautiful, slightly-sexy night of community — where mental health gets to come out from behind closed doors. Open mic. Real stories. Live music. The kind of room where the stigma loses its grip.",
  included: [
    { icon: <Music className="w-5 h-5" />, title: "DJ set by FMG", sub: "Kicks off the night" },
    { icon: <Wine className="w-5 h-5" />, title: "Charcuterie & refreshments", sub: "On us" },
    { icon: <Gift className="w-5 h-5" />, title: "\"Say How You Feel\" goodie bags", sub: "First 20 sign-ups" },
    { icon: <Sparkles className="w-5 h-5" />, title: "5 facial giveaways", sub: "From Vela" },
    { icon: <Mic className="w-5 h-5" />, title: "Live music by Candler Rice", sub: "Closes the night" },
  ],
  mapsUrl: "https://maps.google.com/?q=615+E+Morehead+St+Floor+24+Charlotte+NC+28202",
};

export default function Events() {
  return (
    <div data-testid="events-page" className="bg-white">
      {/* HERO */}
      <section className="py-20 lg:py-28 bg-gradient-hero relative overflow-hidden" data-testid="events-hero">
        <div className="absolute top-10 right-0 w-[500px] h-[500px] bg-teal/10 rounded-full blur-3xl" aria-hidden="true" />
        <div className="relative max-w-4xl mx-auto px-6 lg:px-10 text-center">
          <Reveal>
            <p className="eyebrow mb-5">Events &amp; community</p>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl text-navy leading-[1.05]">
              Where mental health gets to <span className="hl-teal">come out from behind closed doors.</span>
            </h1>
            <p className="mt-7 text-lg sm:text-xl text-muted-ink leading-relaxed max-w-2xl mx-auto">
              Generational Health proudly sponsors and hosts community events that change the conversation —
              warm, honest, occasionally a little sexy. Talking about mental health shouldn't feel like a confession.
              It should feel like a celebration.
            </p>
          </Reveal>
        </div>
      </section>

      {/* FEATURED EVENT */}
      <section className="pb-20 lg:pb-28" data-testid="featured-event-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <Reveal>
            <div className="flex items-center gap-3 mb-10 lg:mb-14">
              <span className="w-2.5 h-2.5 rounded-full bg-teal pulse-dot" />
              <p className="eyebrow">Upcoming &amp; featured</p>
            </div>
          </Reveal>

          <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-start">
            {/* Flyer image */}
            <Reveal className="lg:col-span-5">
              <div className="relative">
                <div className="absolute -inset-5 bg-teal/15 rounded-[2.5rem] blur-3xl" aria-hidden="true" />
                <div className="relative rounded-[2rem] overflow-hidden bg-white border border-slate-200 shadow-2xl">
                  <img
                    src={FLYER_URL}
                    alt="Unfiltered Minds — A nationwide mental health open mic"
                    className="w-full h-auto object-cover"
                    data-testid="event-flyer-image"
                  />
                </div>
              </div>
            </Reveal>

            {/* Details */}
            <Reveal className="lg:col-span-7">
              <p className="eyebrow mb-3 !text-teal">Open Mic · Charlotte</p>
              <h2 className="font-display text-5xl sm:text-6xl lg:text-7xl text-navy leading-[0.95] tracking-tight mb-5">
                {FEATURED_EVENT.name}
                <span className="text-navy/30">.</span>
              </h2>
              <p className="font-display text-2xl sm:text-3xl text-teal italic leading-tight mb-3">
                {FEATURED_EVENT.tagline}
              </p>
              <p className="text-xl text-muted-ink mb-7">{FEATURED_EVENT.subtagline}</p>
              <p className="text-lg text-muted-ink leading-relaxed mb-9 max-w-xl">
                {FEATURED_EVENT.description}
              </p>

              {/* Date / Time / Location strip */}
              <div className="grid sm:grid-cols-3 gap-4 mb-9">
                <div className="p-5 rounded-2xl bg-cream border border-slate-200" data-testid="event-date">
                  <Calendar className="w-5 h-5 text-teal mb-3" />
                  <p className="eyebrow !text-muted-ink mb-1">Date</p>
                  <p className="font-display text-xl text-navy leading-tight">{FEATURED_EVENT.date}</p>
                </div>
                <div className="p-5 rounded-2xl bg-cream border border-slate-200" data-testid="event-time">
                  <Clock className="w-5 h-5 text-teal mb-3" />
                  <p className="eyebrow !text-muted-ink mb-1">Time</p>
                  <p className="font-display text-xl text-navy leading-tight">{FEATURED_EVENT.time}</p>
                </div>
                <div className="p-5 rounded-2xl bg-cream border border-slate-200" data-testid="event-location">
                  <MapPin className="w-5 h-5 text-teal mb-3" />
                  <p className="eyebrow !text-muted-ink mb-1">Where</p>
                  <p className="font-display text-base text-navy leading-tight">
                    {FEATURED_EVENT.location}
                    <br />
                    <span className="text-sm text-muted-ink">{FEATURED_EVENT.city}</span>
                  </p>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 mb-10">
                <a href={FEATURED_EVENT.mapsUrl} target="_blank" rel="noopener noreferrer" className="btn-primary" data-testid="event-directions-button">
                  Get directions <ArrowRight className="w-4 h-4" />
                </a>
                <Link to="/contact" className="btn-outline" data-testid="event-rsvp-button">
                  RSVP / questions
                </Link>
              </div>

              <p className="text-xs text-muted-ink uppercase tracking-[0.2em] font-bold">
                Co-hosted by{" "}
                {FEATURED_EVENT.hosts.map((h, i) => (
                  <span key={i} className="text-navy">
                    {h}
                    {i < FEATURED_EVENT.hosts.length - 1 ? " · " : ""}
                  </span>
                ))}
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* WHAT'S INCLUDED */}
      <section className="py-20 lg:py-24 bg-cream" data-testid="event-included-section">
        <div className="max-w-6xl mx-auto px-6 lg:px-10">
          <Reveal>
            <p className="eyebrow mb-3 text-center">What's included</p>
            <h2 className="font-display text-4xl sm:text-5xl text-navy text-center leading-[1.1] mb-12">
              The good stuff. <span className="hl-teal">All free.</span>
            </h2>
          </Reveal>
          <Reveal stagger className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {FEATURED_EVENT.included.map((item, i) => (
              <div
                key={i}
                className="gh-card !p-6 text-center"
                data-testid={`event-included-${i}`}
              >
                <div className="w-12 h-12 rounded-xl bg-teal-light text-teal flex items-center justify-center mx-auto mb-4">
                  {item.icon}
                </div>
                <p className="font-display text-base text-navy leading-tight mb-2">{item.title}</p>
                <p className="text-xs text-muted-ink leading-relaxed">{item.sub}</p>
              </div>
            ))}
          </Reveal>
        </div>
      </section>

      {/* WHY WE DO THIS */}
      <section className="py-20 lg:py-28" data-testid="event-why-section">
        <div className="max-w-4xl mx-auto px-6 lg:px-10">
          <Reveal className="text-center">
            <Heart className="w-10 h-10 text-teal mx-auto mb-6" fill="currentColor" />
            <p className="eyebrow mb-4">Why we sponsor events</p>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl text-navy leading-[1.05] mb-8">
              You don't fix stigma <span className="hl-teal">in a clinic.</span> You fix it in <span className="hl-teal">a room full of people.</span>
            </h2>
            <p className="text-lg text-muted-ink leading-relaxed mb-3">
              We sponsor and host community events because mental health doesn't change in private — it changes
              when we say it out loud, together. With music. With food. With laughter. With our friends.
            </p>
            <p className="text-lg text-muted-ink leading-relaxed">
              Every event we put our name on has the same goal: make talking about mental health feel like
              the start of something — not the end of something.
            </p>
          </Reveal>
        </div>
      </section>

      <PastEventsGallery />

      {/* EVENT EMAIL SIGNUP */}
      <EventEmailSignup />

      {/* SPONSOR / HOST CTA */}
      <section className="py-24 bg-navy text-white relative overflow-hidden" data-testid="event-sponsor-cta">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-teal/15 rounded-full blur-3xl" aria-hidden="true" />
        <div className="relative max-w-4xl mx-auto px-6 lg:px-10 text-center">
          <p className="eyebrow !text-teal mb-4">Want to bring an event to your city?</p>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] mb-6 text-white">
            Let's <span className="hl-teal">co-create one</span> together.
          </h2>
          <p className="text-lg text-white/80 max-w-2xl mx-auto mb-9">
            We partner with local organizations, schools, employers, and community spaces to host events that
            change the conversation around mental health. If you have a venue, an audience, or just an idea — let's talk.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/partner" className="btn-primary" data-testid="event-partner-cta">
              Partner with us <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/contact" className="btn-outline-teal !border-white !text-white hover:!bg-white hover:!text-navy" data-testid="event-contact-cta">
              Send us a note
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
